import { ReactNode } from "react";
import { logger } from "@siemensplm/common_utils";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  Children,
} from "react";
import DummyCapability from "../components/DummyCapability";
import {
  capabilityType,
  activeCapabilityType,
  CapabilitiesContextType,
} from "../types/CommonTypes";
import { Constants } from "../common/constants";
import CapabilityImagesMap from "./CapabilityImagesMap.json";

const dummyCapability: activeCapabilityType = {
  capabilityDetails: {
    capabilityId: Constants.defaultCapabilityId,
    capabilityName: Constants.defaultCapabilityName,
    capabilityImage: null,
    tabComponent: Constants.defaultTabComponent,
    routeDetails: [],
    toolbarComponent: Constants.defaultToolbarComponent,
    capabilityComponent: Constants.defaultCapabilityName,
    mobileToolbarComponentRow1: Constants.defaultMobileToolbarComponentRow1,
    mobileToolbarComponentRow2: Constants.defaultMobileToolbarComponentRow2,
  },
  routesMap: {},
  componentsMap: {},
  capability: DummyCapability,
  toolbarComponent: () => null,
  mobileToolbarComponentRow1: () => null,
  mobileToolbarComponentRow2: () => null,
};

const capabilityImports: Record<string, () => Promise<any>> = {
  Designcenter: () => import("@siemensplm/design_center"),
  Teamcenter: () => import("@siemensplm/teamcenter"),
  Polarion: () => import("@siemensplm/polarion"),
};

const CapabilitiesContext = createContext<CapabilitiesContextType | null>(null);

export const CapabilitiesProvider = ({ children }: { children: ReactNode }) => {
  const [activeCapability, setActiveCapability] =
    useState<activeCapabilityType>(dummyCapability);
  const [siteCapabilitiesList, setSiteCapabilitiesList] = useState<
    capabilityType[]
  >([]);
  const [isCapabilityReady, setIsCapabilityReady] = useState<boolean>(false);

  const loadCapability = async (
    capabilityName: string,
    capabilityId: string,
    capabilityDomain: string,
    flag: boolean = false
  ) => {
    logger.logInformation(
      `CapabilitiesContext: Loading capability: ${capabilityName}`
    );
    try {
      if (capabilityDomain === "NX MUN Service") {
        capabilityDomain = "Designcenter";
      }
      const capabilityModule = await capabilityImports[capabilityDomain]();
      const capabilityDetails = capabilityModule.domainConfig.capabilities.find(
        (capability: any) => capability.capabilityId === capabilityId
      );
      logger.logInformation(
        `CapabilitiesContext: Capability details found for: ${capabilityName}`
      );

      capabilityDetails.capabilityImage = CapabilityImagesMap.find(
        (capability) =>
          capability.capabilityName === capabilityDetails.capabilityName &&
          capability.capabilityDomain === capabilityDomain
      )?.capabilityImage;

      const toolbarComponent =
        capabilityModule.toolbarComponentsMap[
          capabilityDetails.toolbarComponent
        ];

      const mobileToolbarComponentRow1 =
        capabilityModule.toolbarComponentsMap[
          capabilityDetails.mobiletoolbarComponentRow1
        ];
      const mobileToolbarComponentRow2 =
        capabilityModule.toolbarComponentsMap[
          capabilityDetails.mobiletoolbarComponentRow2
        ];

      const capabilityComponent =
        capabilityModule.capabilityComponentsMap[
          capabilityDetails.capabilityComponent
        ];

      setActiveCapability({
        capabilityDetails: capabilityDetails,
        routesMap: capabilityModule.routesMap,
        componentsMap: capabilityModule.componentsMap,
        capability: capabilityComponent,
        toolbarComponent: toolbarComponent,
        mobileToolbarComponentRow1: mobileToolbarComponentRow1,
        mobileToolbarComponentRow2: mobileToolbarComponentRow2,
      });
      return true;
    } catch (error) {
      logger.logError(
        `CapabilitiesContext: Error loading capability ${capabilityName} - ${error}`
      );
      return false;
    }
  };

  const setDefaultCapability = () => {
    logger.logInformation("CapabilitiesContext: Setting default capability");
    setActiveCapability(dummyCapability);
  };

  useEffect(() => {
    const initializeCapability = async () => {
      const storedCapability = localStorage.getItem(Constants.activeCapability);

      if (storedCapability) {
        const capabilityId = JSON.parse(storedCapability).capabilityId;
        const capabilityName = JSON.parse(storedCapability).capabilityName;
        const capabilityDomain = JSON.parse(storedCapability).capabilityDomain;
        logger.logInformation(
          `CapabilitiesContext: Loading stored active capability: ${capabilityName}`
        );
        await loadCapability(capabilityName, capabilityId, capabilityDomain);
        setIsCapabilityReady(true);
      } else {
        setIsCapabilityReady(true);
      }
    };

    initializeCapability();
  }, []);

  return (
    <CapabilitiesContext.Provider
      value={{
        activeCapability,
        siteCapabilitiesList,
        setSiteCapabilitiesList,
        loadCapability,
        setDefaultCapability,
        isCapabilityReady,
      }}
    >
      {children}
    </CapabilitiesContext.Provider>
  );
};

// Custom hook to use Capabilities context
export const useCapabilitiesContext = () => {
  const context = useContext(CapabilitiesContext);
  if (!context) {
    throw new Error(
      "useCapabilitiesContext must be used within a CapabilitiesProvider"
    );
  }
  return context;
};
